import React from 'react';
import PropTypes from 'prop-types';
import ClickableUsername from './ClickableUsername';
import { formatTime } from '../utils/dateTimeUtils';

export default function UpcomingPlayCard({
  groupName,
  playType,
  format,
  date,
  time,
  location,
  opponent,
  status
}) {
  // Format the date to show day of week
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[date.getDay()];
    return `${dayName}, ${dateStr}`;
  };

  // Calculate if the match is happening soon (within 24 hours)
  const isUpcoming = () => {
    const matchDate = new Date(`${date} ${time}`);
    const now = new Date();
    const hoursDiff = (matchDate - now) / (1000 * 60 * 60);
    return hoursDiff <= 24 && hoursDiff > 0;
  };

  // Get play type badge color
  const getPlayTypeBadge = () => {
    return playType === "Match" 
      ? "bg-purple-100 text-purple-800"
      : "bg-orange-100 text-orange-800";
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition p-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-lg text-textDark">
            {formatDate(date)} at {formatTime(time)}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-1 rounded-full text-xs ${getPlayTypeBadge()}`}>
              {playType}
            </span>
            {format && (
              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                {format}
              </span>
            )}
          </div>
        </div>
        {isUpcoming() && (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            Coming Up
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center text-sm text-textMed">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span>{groupName}</span>
        </div>
        
        <div className="flex items-center text-sm text-textMed">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{location}</span>
        </div>

        {opponent && (
          <div className="flex items-center text-sm text-textMed">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="flex items-center gap-2">
              <span>vs</span>
              <ClickableUsername user={opponent} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Empty state component
export function UpcomingPlayEmptyState() {
  return (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <div className="text-center">
        <div className="font-bold text-2xl">
          <div className="text-black">No Upcoming</div>
          <div className="text-[#0D9488]">Plays</div>
        </div>
        <p className="text-gray-600 mt-4">Join a group and propose a play to get started!</p>
      </div>
    </div>
  );
}

UpcomingPlayCard.propTypes = {
  groupName: PropTypes.string.isRequired,
  playType: PropTypes.string.isRequired,
  format: PropTypes.string,
  date: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  opponent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    skillLevel: PropTypes.string.isRequired
  }),
  status: PropTypes.string.isRequired
}; 